import type { NextPage } from "next"
import { useEffect } from "react"
import { formatDate } from "src/utils"
import { Container } from "src/layout/container/Container"
import ModuleList from "src/components/module-list/ModuleList"
import BlogPostGridModule from "src/modules/blog-post-grid-module/BlogPostGridModule"
import cn from "classnames"
import s from "./blogPost.module.scss"
import { BaseQueryProps } from ".."
import { useOverwrites } from "../../layout/page/overwriteContext"
import FullscreenImageModule from "../../modules/fullscreen-image-module/FullscreenImageModule"
import { InternalLink, BaseModule } from "src/sanity/types"
import { QueriedBlogPost, TeaserBlogPost, BlogTableOfContents } from "./blogHelper"
import { getTranslation } from "src/lib/translations"
import { generateSanityImage } from "src/sanity/image"
import buildLink from "src/sanity/buildLink"

// We query for mainImage and modules in a specific way, so we manually define them
export interface BlogPostDocument extends QueriedBlogPost, BaseQueryProps {
    modules: BaseModule[]
    relatedBlogPosts: TeaserBlogPost[]
    link: InternalLink
    updatedAt?: string
}

const Template: NextPage<{ pageData: BlogPostDocument }> = ({ pageData }) => {
    const { headerDownloadCTA: headerDownloadLink } = useOverwrites()
    const t = getTranslation("blog", pageData.lang)

    useEffect(() => {
        if (pageData.headerCTAOverwrite?.style) {
            headerDownloadLink.setCTA(pageData.headerCTAOverwrite)
        }
    }, [headerDownloadLink, pageData.headerCTAOverwrite])

    return (
        <article className={cn(s["blog-post"])}>
            <div style={{ position: "relative" }}>
                <FullscreenImageModule className={cn(s["blog-post__main-image"])} media={pageData.mainImage} titlePosition="top" />
                <section className={cn(s["blog-post__intro-wrapper"])}>
                    <div className={cn(s["blog-post__intro-wrapper__background"])}>
                        <Container className={cn(s["blog-post__intro"])}>
                            <div className={cn(s["blog-post__intro-inner"])}>
                                <div className={cn("label-100", s["blog-post__intro-inner__meta"])}>
                                    <span className={cn(s["blog-post__intro-inner__meta__category"])}>{pageData.category.title}</span>
                                    <span>{t.published} <time dateTime={pageData.publishedAt}>{formatDate(new Date(pageData.publishedAt), pageData.lang)}</time></span>
                                    {pageData.updatedAt && (<span>{t.updated} <time dateTime={pageData.updatedAt}>{formatDate(new Date(pageData.updatedAt), pageData.lang)}</time></span>)}
                                </div>
                                <h1 className={cn("heading-100")}>{pageData.title}</h1>
                                {pageData.author && <address>{t.byAuthor} {pageData.author}</address>}
                                <p className={cn(s["blog-post__intro-inner__intro"], "label-200")}>{pageData.intro}</p>

                            </div>
                        </Container>
                    </div>
                </section>
            </div>
            <section className={cn(s["blog-post__module-list"])}>
                <BlogTableOfContents modules={pageData.modules} title={t.tableOfContentsTitle} className={cn(s["blog-post__table-of-contents"])} />
                <ModuleList modules={pageData?.modules} pageThemeType={pageData?.pageThemeType} updatedAt={pageData?.updatedAt} />
            </section>

            {/* If there is not added manual blog post grid in the blog post, we show a generic one based on the category (relatedBlogPosts) */}
            {!pageData?.modules?.some(module => module.type === "blogPostGridModule") && pageData.relatedBlogPosts?.length > 0 && (
                <BlogPostGridModule blogPosts={pageData.relatedBlogPosts} columns={3} title={t.relatedBlogPostsTitle} />
            )}
            <script type='application/ld+json'
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": pageData.title.slice(0, 109),
                        "image": generateSanityImage({ image: pageData.mainImage, width: 1920, height: 1080, step: 500 })?.srcSet.map(src => src.split(" ")[0]),
                        ...(pageData.author
                            ? {
                                editor: pageData.author,
                                author: { "@type": "Person", name: pageData.author },
                            }
                            : {
                                author: {
                                    "@type": "Organization",
                                    name: "Lunar",
                                    url: "https://www.lunar.app",
                                },
                            }),
                        "genre": pageData.category.englishTitle,
                        "publisher": {
                            "@type": "Organization",
                            name: "Lunar",
                            url: "https://www.lunar.app",
                        },
                        "url": buildLink(pageData.link),
                        "datePublished": pageData.publishedAt,
                        "dateModified": pageData._updatedAt,
                        "description": pageData.intro,
                    }),
                }} >
            </script>
        </article>
    )
}

export default Template
